// src/utils/consult/mediaTelemetry.ts

export type CandidateType = "host" | "srflx" | "prflx" | "relay" | "unknown";
export type AudioLevelBucket = "none" | "low" | "mid" | "high";

export interface TrackState {
	readyState?: "live" | "ended";
	muted?: boolean;
	enabled?: boolean;
}

export interface ElementState {
	paused: boolean;
	readyState: number;
	videoWidth: number;
	muted: boolean;
}

export interface PeerStatsSample {
	pair?: { local: CandidateType; remote: CandidateType };
	in?: {
		audio?: { bytes?: number; packets?: number; packetsLost?: number; level?: AudioLevelBucket };
		video?: { bytes?: number; packets?: number; frames?: number; width?: number };
	};
	out?: {
		audio?: { bytes?: number; packets?: number };
		video?: { bytes?: number; packets?: number; frames?: number };
	};
}

export type StatsBaseline = Record<string, number>;

export interface PeerTelemetry extends PeerStatsSample {
	peer: string;
	connectionState: RTCPeerConnectionState;
	element?: ElementState;
	remoteTracks?: { audio?: TrackState; video?: TrackState };
}

export interface WebrtcStatsPayload {
	roomId: string;
	v: 1;
	kind: "webrtc_stats";
	trigger: "interval" | "state_change";
	local: { audio?: TrackState; video?: TrackState };
	peers: PeerTelemetry[];
}

type Stat = Record<string, unknown>;

const CANDIDATE_TYPES = new Set<string>(["host", "srflx", "prflx", "relay"]);

const num = (value: unknown): number | undefined => (typeof value === "number" && Number.isFinite(value) ? value : undefined);

// Drops undefined keys and returns undefined for an empty section, so an unreadable field is omitted, never 0.
const compact = <T extends object>(value: T): T | undefined => {
	const entries = Object.entries(value).filter(([, v]) => v !== undefined);
	return entries.length ? (Object.fromEntries(entries) as T) : undefined;
};

export const bucketAudioLevel = (level: number): AudioLevelBucket => (level <= 0.001 ? "none" : level < 0.01 ? "low" : level < 0.1 ? "mid" : "high");

export const describeElement = (video: HTMLVideoElement): ElementState => ({
	paused: video.paused,
	readyState: video.readyState,
	videoWidth: video.videoWidth,
	muted: video.muted,
});

export const describeTrack = (track: MediaStreamTrack): TrackState =>
	compact({
		readyState: track.readyState === "live" || track.readyState === "ended" ? track.readyState : undefined,
		muted: typeof track.muted === "boolean" ? track.muted : undefined,
		enabled: typeof track.enabled === "boolean" ? track.enabled : undefined,
	}) ?? {};

export const describeTracks = (stream: { getTracks: () => MediaStreamTrack[] } | null | undefined): { audio?: TrackState; video?: TrackState } | undefined => {
	if (!stream || typeof stream.getTracks !== "function") return undefined;
	const tracks = stream.getTracks();
	const audio = tracks.find((track) => track.kind === "audio");
	const video = tracks.find((track) => track.kind === "video");
	return compact({ audio: audio && describeTrack(audio), video: video && describeTrack(video) });
};

export function summarisePeerStats(report: RTCStatsReport, prev?: StatsBaseline): { sample: PeerStatsSample; baseline: StatsBaseline } {
	const byId = new Map<string, Stat>();
	const totals: Record<string, number> = {};
	let audioLevel: number | undefined;
	let nominatedPair: Stat | undefined;
	let transport: Stat | undefined;

	const add = (key: string, value: unknown) => {
		const n = num(value);
		if (n !== undefined) totals[key] = (totals[key] ?? 0) + n;
	};

	report.forEach((stat: Stat) => {
		byId.set(String(stat.id), stat);
		const kind = stat.kind === "audio" || stat.kind === "video" ? stat.kind : undefined;
		if (stat.type === "inbound-rtp" && kind) {
			add(`in.${kind}.bytes`, stat.bytesReceived);
			add(`in.${kind}.packets`, stat.packetsReceived);
			if (kind === "audio") {
				add("in.audio.packetsLost", stat.packetsLost);
				const level = num(stat.audioLevel);
				if (level !== undefined) audioLevel = Math.max(audioLevel ?? 0, level);
			} else {
				add("in.video.frames", stat.framesDecoded);
				const width = num(stat.frameWidth);
				if (width !== undefined) totals["in.video.width"] = Math.max(totals["in.video.width"] ?? 0, width);
			}
		} else if (stat.type === "outbound-rtp" && kind) {
			add(`out.${kind}.bytes`, stat.bytesSent);
			add(`out.${kind}.packets`, stat.packetsSent);
			if (kind === "video") add("out.video.frames", stat.framesSent);
		} else if (stat.type === "candidate-pair" && stat.nominated === true && stat.state === "succeeded" && !nominatedPair) {
			nominatedPair = stat;
		} else if (stat.type === "transport" && !transport) {
			transport = stat;
		}
	});

	const baseline: StatsBaseline = {};
	const delta = (key: string): number | undefined => {
		const current = totals[key];
		if (current === undefined) return undefined;
		baseline[key] = current;
		const before = prev?.[key];
		return before === undefined ? current : Math.max(0, current - before);
	};

	const selectedPair = nominatedPair ?? (transport?.selectedCandidatePairId ? byId.get(String(transport.selectedCandidatePairId)) : undefined);
	// candidateType ONLY: the candidate stats also carry address/ip/port/url, which identify the participant.
	const candidateTypeOf = (id: unknown): CandidateType => {
		const type = byId.get(String(id))?.candidateType;
		return typeof type === "string" && CANDIDATE_TYPES.has(type) ? (type as CandidateType) : "unknown";
	};

	const sample: PeerStatsSample = compact({
		pair: selectedPair ? { local: candidateTypeOf(selectedPair.localCandidateId), remote: candidateTypeOf(selectedPair.remoteCandidateId) } : undefined,
		in: compact({
			audio: compact({
				bytes: delta("in.audio.bytes"),
				packets: delta("in.audio.packets"),
				packetsLost: delta("in.audio.packetsLost"),
				level: audioLevel === undefined ? undefined : bucketAudioLevel(audioLevel),
			}),
			video: compact({
				bytes: delta("in.video.bytes"),
				packets: delta("in.video.packets"),
				frames: delta("in.video.frames"),
				width: totals["in.video.width"],
			}),
		}),
		out: compact({
			audio: compact({ bytes: delta("out.audio.bytes"), packets: delta("out.audio.packets") }),
			video: compact({ bytes: delta("out.video.bytes"), packets: delta("out.video.packets"), frames: delta("out.video.frames") }),
		}),
	}) ?? {};

	return { sample, baseline };
}
